import { watch } from "vue";
import type { DRLocalDrawingMeta } from "@/types";
import { useDrawingStore } from "@/stores/fileStore";
import { useDeviceState } from "@/stores/DeviceState";
import { syncer } from "@/repositories/Syncer";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { activityEventRepository } from "@/repositories/activityEvent.repository";
import { backupRepository } from "@/repositories/backup.repository";
import { selectEvictableLocalDrawings } from "@/utils/drawings.utils";
import { LOCAL_DRAWING_MAX_IDLE_MS } from "@/utils/constants";
import { Logger } from "@/logger";

/**
 * Intervalle minimal entre deux passes. `AuthLayout` est démonté puis remonté à
 * chaque aller-retour vers le canvas (celui-ci vit hors du layout, cf. router.ts) :
 * sans ce plancher, la purge se déclencherait à chaque fermeture de dessin.
 *
 * Le compteur n'est PAS persisté : un rechargement de page relance donc une passe,
 * comme `backupRepository.pruneOlderThan` qui tourne à chaque entrée dans l'app. Le
 * plancher borne les sessions longues (PWA laissée ouverte plusieurs jours), pas les
 * rechargements — et une passe ne coûte qu'une requête indexée sur l'oplog plus un
 * filtre en mémoire sur des métadonnées déjà chargées.
 */
const RETENTION_MIN_INTERVAL_MS = 24 * 60 * 60 * 1000; // 1 jour

/** État partagé par tous les montages du composable (cf. RETENTION_MIN_INTERVAL_MS). */
let lastRunAt = 0;
let isRunning = false;

/**
 * Les métadonnées sont passées en paramètre plutôt que relues depuis le store :
 * la fonction s'exécute après plusieurs `await`, donc hors du contexte de setup
 * du composant appelant.
 */
async function runRetentionPass(
  localDrawings: DRLocalDrawingMeta[]
): Promise<void> {
  if (!localDrawings.length) return;

  const unsyncedDrawingIds =
    await activityEventRepository.getDrawingIdsWithUnsyncedEvents();

  const evictableIds = selectEvictableLocalDrawings(
    localDrawings,
    unsyncedDrawingIds,
    LOCAL_DRAWING_MAX_IDLE_MS,
    Date.now()
  );
  if (!evictableIds.length) return;

  // Seule `db.drawings` est touchée : la ligne `db.files` reste en place, donc le
  // dessin continue d'apparaître dans les listes et repasse simplement au badge
  // « En ligne uniquement », re-téléchargeable d'un tap (cf. DrawingInfo.vue).
  await localDrawingRepository.deleteManyById(evictableIds);
  await backupRepository.deleteByDrawingIds(evictableIds);

  // Les images de ces dessins deviennent orphelines : `Syncer.removeUnusedImagesInDb`
  // les récupère au tick de sync suivant (≤ 10 s), inutile de les traiter ici.
  Logger.warn(
    `[LocalDrawingsRetention] Retiré ${evictableIds.length} dessin(s) du stockage local (inactifs depuis plus de ${LOCAL_DRAWING_MAX_IDLE_MS}ms)`
  );
}

/**
 * Politique de rétention du stockage hors ligne : retire de `db.drawings` les dessins
 * inactifs depuis plus de `LOCAL_DRAWING_MAX_IDLE_MS` et ne portant aucun travail non
 * synchronisé (cf. `selectEvictableLocalDrawings` pour le détail des gardes). Purge
 * silencieuse — rien n'est perdu, le contenu est déjà sur le serveur.
 *
 * Conditions d'exécution, au-delà des gardes par dessin :
 *  - EN LIGNE obligatoire : évincer hors connexion rendrait le dessin inaccessible
 *    jusqu'à la reconnexion, exactement au moment où l'utilisateur en a besoin.
 *  - Pas de sync en cours : `autoSync` écrit sur les mêmes tables, et les
 *    métadonnées lues ici seraient périmées d'un instant à l'autre.
 *
 * La sélection ne coûte aucune lecture IDB supplémentaire : elle réutilise les
 * `DRLocalDrawingMeta` (sans binaires) déjà maintenus par le liveQuery de `fileStore`.
 */
export function useLocalDrawingsRetention(): void {
  const drawingStore = useDrawingStore();
  const deviceState = useDeviceState();

  watch(
    () => [drawingStore.localDrawings, deviceState.isOnline] as const,
    async ([localDrawings, isOnline]) => {
      if (!localDrawings || !isOnline) return;
      if (isRunning || syncer.isSyncing.value) return;
      if (Date.now() - lastRunAt < RETENTION_MIN_INTERVAL_MS) return;

      isRunning = true;
      lastRunAt = Date.now();
      try {
        await runRetentionPass(localDrawings);
      } catch (e) {
        Logger.error(
          `[LocalDrawingsRetention] Échec de la purge : ${JSON.stringify(e)}`
        );
      } finally {
        isRunning = false;
      }
    },
    { immediate: true }
  );
}
